import { addToast } from "components/ToastContainer/ToastStore";
import { routeConstants } from "constants/routeConstants";
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAppSelector } from "slice";
import { useGetCountriesQuery, useGetLanguagesQuery } from "slice/common";
import {
    useAddEditLanguagesMutation,
    useDeleteUserLanguageMutation,
    useGetProfileStrengthQuery,
    useGetUserLanguagesQuery,
    useGetUserLocationQuery,
    useSetUserLocationMutation,
} from "slice/user";
import { validations } from "utils/validations";
import { UserLanguage } from "./types";

export const useLocationAndLanguages = () => {
    const { id: user_id } = useAppSelector((state) => state.user);
    const { data: countries } = useGetCountriesQuery({});
    const { data: languagesList, isSuccess: fetchedLanguages } =
        useGetLanguagesQuery({});
    const {
        data: userLanguagesData,
        refetch: refetchUserLanguages,
        isLoading: fetchingUserLanguages,
        isSuccess: userLanguagesSuccess,
    } = useGetUserLanguagesQuery(user_id, {
        refetchOnMountOrArgChange: true,
        skip: !fetchedLanguages,
    });
    const [clickedOnNext, setClickedOnNext] = useState(false);
    const {
        data: userLocationData,
        isLoading: fetchingUserLocation,
        refetch: refetchLocationData,
    } = useGetUserLocationQuery(user_id, {
        refetchOnMountOrArgChange: true,
    });
    const [
        setUserLocationMutation,
        {
            isSuccess: isUserLocationMutationSuccess,
            isLoading: submittingLocation,
        },
    ] = useSetUserLocationMutation();
    const [
        addEditLanguages,
        { isSuccess: isAddEditLangSuccess, isLoading: submittingLanguages },
    ] = useAddEditLanguagesMutation();
    const [deleteLanguage, { isSuccess: languageDeleted }] =
        useDeleteUserLanguageMutation();
    const { refetch: refetchProfileStrength } = useGetProfileStrengthQuery(
        user_id ?? "",
        {
            skip: !user_id,
        }
    );
    const [countriesData, setCountriesData] = useState<any>([]);
    const [languageData, setLanguageData] = useState([]);
    const [userLocation, setUserLocation] = useState({
        country: "",
        city: "",
    });
    const [userLocationErrors, setUserLocationErrors] = useState({
        country: "",
        city: "",
    });
    const [userLanguages, setUserLanguages] = useState<UserLanguage[]>([]);
    const [languageMethod, setLanguageMethod] = useState("POST");
    const [locationMethod, setLocationMethod] = useState("POST");
    const [isModalOpen, setIsModalOpen] = useState(false);
    const navigateTo = useNavigate();

    const toggleModal = () => setIsModalOpen((prev) => !prev);

    const handleChangeLanguages = (id: string, name: string, value: string) => {
        const index = userLanguages.findIndex((lang) => lang.id === id);
        let data = userLanguages[index];

        if (data) {
            setUserLanguages((prev) => {
                const newData = [...prev];
                newData[index] = {
                    ...data,
                    language_error: validations("language_id", value),
                    [name]: value,
                };
                return newData;
            });
        }
    };

    const addLanguage = () => {
        setUserLanguages((prev) => [
            ...prev,
            {
                id: new Date().getTime().toString(),
                language_id: "",
                language_error: "",
                level: "beginner",
            },
        ]);
    };

    const handleChangeCity = (e: React.ChangeEvent<HTMLInputElement>) => {
        setUserLocation((prev) => ({
            ...prev,
            city: e.target.value,
        }));

        setUserLocationErrors((prev) => ({
            ...prev,
            city: validations("city", e.target.value),
        }));
    };

    const handleChangeCountry = (value: any) => {
        setUserLocation((prev) => ({
            ...prev,
            country: value,
        }));

        setUserLocationErrors((prev) => ({
            ...prev,
            country: validations("country", value),
        }));
    };

    const handleBack = () => {
        navigateTo(
            routeConstants.profile.editProfile.root +
                routeConstants.profile.editProfile.otherInterests
        );
    };

    const handleSaveAndNext = () => {
        setClickedOnNext(true);
        handleSave();
    };

    const handleSave = () => {
        const cityError = validations("city", userLocation.city);
        const countryError = validations("country", userLocation.country);

        setUserLocationErrors({
            city: cityError,
            country: countryError,
        });

        let hasLanguageError = false;

        const updatedLanguagesData = userLanguages.map((language) => {
            const error = validations("language_id", language.language_id);

            if (error !== "") {
                hasLanguageError = true;
            }

            return {
                ...language,
                language_error: error,
            };
        });

        setUserLanguages(updatedLanguagesData);

        if (cityError === "" && countryError === "" && !hasLanguageError) {
            const user_languages = userLanguages.map(
                ({ language_id, level }) => ({
                    language_id,
                    level,
                })
            );

            setUserLocationMutation({
                user_id,
                method: locationMethod,
                data: {
                    country_id: userLocation.country,
                    city: userLocation.city,
                },
            });

            addEditLanguages({
                user_id,
                method: languageMethod,
                data: {
                    user_languages,
                },
            }).then(() => {
                refetchProfileStrength();
            });
        }
    };

    const removeLanguage = (id: string) => {
        if (languageMethod === "POST") {
            setUserLanguages(userLanguages.filter((lang) => lang.id !== id));
        } else {
            deleteLanguage({
                user_id,
                user_language_id: id,
            });
        }
    };

    useEffect(() => {
        if (userLanguagesData && userLanguagesData.length > 0) {
            setLanguageMethod("PUT");
            setUserLanguages(
                userLanguagesData.map((lang) => ({
                    language_id: lang.language_id,
                    level: lang.level,
                    language_error: "",
                    id: lang.id,
                }))
            );
        } else if (userLanguagesSuccess) {
            setUserLanguages([
                {
                    id: new Date().getTime().toString(),
                    language_id: "",
                    language_error: "",
                    level: "beginner",
                },
            ]);
        }
    }, [userLanguagesData, userLanguagesSuccess]);

    useEffect(() => {
        if (languagesList && languagesList.length > 0) {
            setLanguageData(
                languagesList.map((language: any, i: number) => ({
                    label: language.name,
                    value: language.id,
                }))
            );
        }
    }, [languagesList]);

    useEffect(() => {
        if (userLocationData) {
            setLocationMethod("PUT");
            setUserLocation({
                city: userLocationData.city,
                country: userLocationData.country_id,
            });
        }
    }, [userLocationData]);

    useEffect(() => {
        if (countries && countries.length) {
            setCountriesData(
                countries.map((country) => ({
                    label: country.name,
                    value: country.id,
                }))
            );
        }
    }, [countries]);

    useEffect(() => {
        if (isUserLocationMutationSuccess && isAddEditLangSuccess) {
            addToast({
                type: "success",
                text: "Data saved successfully!",
            });
            refetchUserLanguages();
            refetchLocationData();
            if (clickedOnNext) {
                navigateTo(
                    routeConstants.profile.editProfile.root +
                        routeConstants.profile.editProfile.contactDetails
                );
            }
        }
    }, [
        clickedOnNext,
        navigateTo,
        isAddEditLangSuccess,
        isUserLocationMutationSuccess,
        refetchLocationData,
        refetchUserLanguages,
    ]);

    useEffect(() => {
        if (languageDeleted) {
            refetchUserLanguages();
        }
    }, [languageDeleted, refetchUserLanguages]);

    return {
        userLanguages,
        countriesData,
        isLoading: fetchingUserLanguages || fetchingUserLocation,
        isSubmitting: submittingLanguages || submittingLocation,
        languageData,
        handleSaveAndNext,
        addLanguage,
        handleSave,
        removeLanguage,
        handleChangeLanguages,
        userLocation,
        handleChangeCity,
        clickedOnNext,
        handleChangeCountry,
        handleBack,
        isModalOpen,
        toggleModal,
        userLocationErrors,
    };
};
